import { Link } from "react-router-dom";
import type { Video } from "@/mocks/videos";

interface VideoCardProps {
  video: Video;
  variant?: "grid" | "compact";
}

export default function VideoCard({ video, variant = "grid" }: VideoCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/detail/${video.id}`}
        className="group flex w-[240px] shrink-0 flex-col overflow-hidden rounded-lg border border-background-800 bg-background-900 transition-colors hover:border-accent-500/70 cursor-pointer md:w-[260px]"
      >
        <div className="relative h-[150px] w-full overflow-hidden md:h-[160px]">
          <img
            src={video.backdrop}
            alt={`${video.title} - 多多视频精彩影视推荐`}
            title={`${video.title} - 多多视频`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent" />
          <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-background-950/80 px-2 py-1 text-xs font-semibold text-accent-400">
            <i className="ri-star-fill text-xs" />
            {video.rating.toFixed(1)}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-3">
          <h3 className="line-clamp-1 text-sm font-semibold text-foreground-50 group-hover:text-accent-400">
            {video.title}
          </h3>
          <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-400">
            {video.description}
          </p>
          <div className="mt-auto pt-2.5 text-[11px] text-secondary-400">
            {video.region} · {video.year}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${video.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-800 bg-background-900 transition-all hover:border-accent-500/70 cursor-pointer"
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden">
        <img
          src={video.cover}
          alt={`${video.title} - 多多视频高清影视海报`}
          title={`${video.title} - 多多视频`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/10 to-transparent" />
        <span className="absolute left-2 top-2 rounded-md bg-primary-500/90 px-2 py-0.5 text-[11px] font-semibold text-background-50">
          {video.categoryName}
        </span>
        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-background-950/80 px-2 py-1 text-xs font-semibold text-accent-400">
          <i className="ri-star-fill text-xs" />
          {video.rating.toFixed(1)}
        </span>
        <span className="absolute bottom-2 left-2 rounded-md bg-background-950/80 px-2 py-0.5 text-[11px] font-medium text-foreground-200">
          {video.status}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-3 md:p-4">
        <h3 className="line-clamp-1 text-sm font-semibold text-foreground-50 transition-colors group-hover:text-accent-400 md:text-base">
          {video.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-400">
          {video.description}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {video.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="whitespace-nowrap rounded-md bg-secondary-100/10 px-2 py-0.5 text-[11px] text-secondary-300"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-background-800 pt-2.5 text-[11px] text-secondary-400">
          <span>{video.region}</span>
          <span>{video.updatedAt} 更新</span>
        </div>
      </div>
    </Link>
  );
}